import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';
import queryString from 'query-string';
import axios from 'config/axios';
import Auth from 'Api/Auth';
import Users from 'Api/Users';
import { parseJwt } from 'utils';
import { setCurrentTeacher } from 'store/actions/authActions';
import store from 'store';

/**
 * Options for useAuth hook
 */
export interface UseAuthOptions {
    embed?: boolean;
}

/**
 * Redux state type for auth
 */
interface AuthState {
    auth?: {
        isAuthenticated?: boolean;
        expiryDate?: string;
        teacher?: {
            id?: number;
            role?: number;
            [key: string]: any;
        };
    };
}

/**
 * Return type for useAuth hook
 */
export interface UseAuthReturn {
    isAuthenticated: boolean;
    isExpired: boolean;
    user: any;
    role?: number;
    checkAuth: () => boolean;
    hasRole: (requiredRole: number | number[]) => boolean;
    hasAnyRole: (roles: number[]) => boolean;
    hasRoleOrHigher: (minRole: number) => boolean;
    redirectUri?: string | string[] | null;
}

/**
 * Custom hook for authentication logic
 * Handles authentication state, token refresh, and SSO flows
 */
export const useAuth = (options: UseAuthOptions = {}): UseAuthReturn => {
    const auth = useSelector((state: AuthState) => state.auth);
    const location = useLocation();
    const navigate = useNavigate();
    const { embed = false } = options;

    const { redirectUri, _rt } = queryString.parse(new URL(window.location.href).search);

    const isAuthenticated = auth?.isAuthenticated ?? false;
    const isExpired = !moment().isBefore(moment(auth?.expiryDate));
    const user = auth?.teacher;
    const role = auth?.teacher?.role;

    // Handle token refresh
    useEffect(() => {
        if (_rt && !embed) {
            axios.defaults.headers.common['x-refresh-token'] = _rt as string;
            localStorage.setItem('lsgoRT', _rt as string);
            Auth.userV2({
                redirect: true,
                history: { push: navigate, location },
                redirectUri: redirectUri as string,
                lsgoRT: _rt as string,
            } as any).then(async ({ token, intercomToken }: { token?: string; intercomToken?: string }) => {
                if (token) {
                    const decoded = await parseJwt(token.replace('Bearer ', ''));
                    if (decoded.id) {
                        try {
                            const profile = await Users.getUserProfile(decoded.id);
                            if (!profile.data.name) profile.data.name = profile.data.firstName;
                            store.dispatch(setCurrentTeacher({ ...profile.data, alias: decoded.alias }));
                        } catch (error) {
                            console.log(error, 'error');
                        }
                    }
                    setTimeout(() => navigate((redirectUri as string) || '/'), 500);
                }
            });
        }
    }, [_rt, embed, navigate, location, redirectUri]);

    /**
     * Check if user is authenticated and account is not expired
     */
    const checkAuth = (): boolean => {
        return isAuthenticated && !isExpired;
    };

    /**
     * Check if user has a specific role
     */
    const hasRole = (requiredRole: number | number[]): boolean => {
        if (!role) return false;
        if (Array.isArray(requiredRole)) {
            return requiredRole.includes(role);
        }
        return role === requiredRole;
    };

    /**
     * Check if user has any of the specified roles
     */
    const hasAnyRole = (roles: number[]): boolean => {
        if (!role || !roles || roles.length === 0) return false;
        return roles.includes(role);
    };

    /**
     * Check if user has role or higher (lower number = higher privilege)
     */
    const hasRoleOrHigher = (minRole: number): boolean => {
        if (!role) return false;
        return role <= minRole;
    };

    return {
        isAuthenticated,
        isExpired,
        user,
        role,
        checkAuth,
        hasRole,
        hasAnyRole,
        hasRoleOrHigher,
        redirectUri,
    };
};
